import { useEffect, useRef } from 'react';
import { match } from 'ts-pattern';

type ScriptResource = {
  script: HTMLScriptElement;
  status: 'pending' | 'loaded' | 'error';
};

/** Mapping of `src` URLs to the corresponding `ScriptResource`. */
const scriptResources = new Map<string, ScriptResource>();

function getOrCreateScriptResource(src: string) {
  const existing = scriptResources.get(src);
  if (existing) return existing;

  const script = document.createElement('script');
  script.async = true;

  const resource: ScriptResource = { script, status: 'pending' };
  scriptResources.set(src, resource);

  script.addEventListener('load', () => {
    resource.status = 'loaded';
  });
  script.addEventListener('error', () => {
    resource.status = 'error';
  });

  script.src = src; // set last, so listeners are wired up before the request is made, to handle cases where it resolves from the cache immediately/synchronously

  document.head.appendChild(script);

  return resource;
}

/**
 * @deprecated When we upgrade to React 19, we can use the `<head>` hoisting dedupe behavior for the `<script>` component.
 * @see https://react.dev/reference/react-dom/components/script
 *
 * Renders a `<script>` tag for the `src` provided, guaranteeing only one instance of it is loaded by the component.
 * This ensures that multiple instances of the `Script` component with the same `src` do not result in multiple network requests or duplicate script tags.
 * A settled script will never be unmounted, reflecting how once a script is loaded its side effects remain.
 * Multiple consumers will still receive the appropriate `onLoad` or `onError` callbacks based on the script's status, even if they mount after the script has already settled.
 */
export default function Script({
  src,
  onLoad,
  onError,
}: {
  src: string;
  onLoad?: () => void;
  onError?: () => void;
}) {
  // leveraging refs, so the effect doesn't need to depend on `onLoad` and `onError` directly and instead always has the latest values, and the effect is only dependent on the `src`

  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const scriptResource = getOrCreateScriptResource(src);

    const handleLoad = () => onLoadRef.current?.();
    const handleError = () => onErrorRef.current?.();

    return match(scriptResource)
      .with({ status: 'loaded' }, () => {
        handleLoad();
        return undefined;
      })
      .with({ status: 'error' }, () => {
        handleError();
        return undefined;
      })
      .with({ status: 'pending' }, ({ script }) => {
        script.addEventListener('load', handleLoad);
        script.addEventListener('error', handleError);

        return () => {
          script.removeEventListener('load', handleLoad);
          script.removeEventListener('error', handleError);
        };
      })
      .exhaustive();
  }, [src]);

  return null;
}
